import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import LazyImage from './LazyImage';

const Gallery = ({ title, images, categoryLink }) => {
  const scrollContainerRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 350;
      // In RTL mode, scrolling is inverted
      container.scrollBy({
        left: direction === 'right' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const openLightbox = (imageUrl, index) => {
    setSelectedImage({ image: imageUrl, index, title });
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateLightbox = (direction) => {
    if (!selectedImage) return;
    const newIndex = direction === 'next' 
      ? (selectedImage.index + 1) % images.length 
      : (selectedImage.index - 1 + images.length) % images.length;
    
    const newImage = images[newIndex];
    const newImageUrl = typeof newImage === 'string' ? newImage : newImage.url;
    
    setSelectedImage({
      image: newImageUrl,
      index: newIndex,
      title
    });
  };

  return (
    <>
      <div className="mb-20 group">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to={categoryLink} 
            className="text-4xl font-bold text-amber-900 hover:text-amber-700 transition-all duration-300 cursor-pointer relative group flex flex-col"
          >
            {title}
            <span className="text-sm text-amber-600 font-normal mt-1 opacity-70 group-hover:opacity-100 transition-opacity duration-300">
              צפה בכל התמונות ←
            </span>
            <div className="absolute bottom-0 left-0 w-0 h-1 bg-amber-600 transition-all duration-300 group-hover:w-full"></div>
          </Link>
          <div className="flex space-x-3 space-x-reverse">
            <button 
              onClick={() => scroll('left')}
              className="p-3 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 hover:from-amber-200 hover:to-amber-300 transition-all duration-300 transform hover:scale-110 shadow-md hover:shadow-lg"
              aria-label="גלול שמאלה בגלריה"
            >
              <ChevronRight className="w-6 h-6 text-amber-800" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-3 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 hover:from-amber-200 hover:to-amber-300 transition-all duration-300 transform hover:scale-110 shadow-md hover:shadow-lg"
              aria-label="גלול ימינה בגלריה"
            >
              <ChevronLeft className="w-6 h-6 text-amber-800" />
            </button>
          </div>
        </div>
        
        <div 
          ref={scrollContainerRef}
          className="flex space-x-6 space-x-reverse overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {images.map((image, index) => {
            const imageUrl = typeof image === 'string' ? image : image.url;
            const imageTitle = typeof image === 'string' ? `${title} ${index + 1}` : image.title;
            const imageWidth = typeof image === 'object' ? image.width : undefined;
            const imageHeight = typeof image === 'object' ? image.height : undefined;
            const aspectRatio = typeof image === 'object' ? image.aspectRatio : 'aspect-[4/3]';
            
            return (
              <div 
                key={index}
                className="flex-shrink-0 relative group cursor-pointer"
                onClick={() => openLightbox(imageUrl, index)}
              >
                <div className={`w-96 h-72 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-rotate-1 ${aspectRatio}`}>
                  <LazyImage 
                    src={imageUrl} 
                    alt={imageTitle}
                    width={imageWidth}
                    height={imageHeight}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100" />
                  </div>
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  {index + 1}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-50 bg-gradient-to-br from-black/85 via-black/90 to-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-5xl max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              aria-label="סגירת חלון תמונה"
              className="absolute -top-12 right-0 z-10 bg-black/50 hover:bg-black/70 text-white hover:text-amber-400 transition-all duration-300 rounded-full p-2 backdrop-blur-sm border border-white/20"
            >
              <X className="w-8 h-8" />
            </button>

            <img
              src={selectedImage.image}
              alt={`${selectedImage.title} ${selectedImage.index + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />

            <button
              onClick={() => navigateLightbox('prev')}
              aria-label="תמונה קודמת"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white hover:text-amber-400 transition-all duration-300 rounded-full p-3 backdrop-blur-sm border border-white/20 shadow-lg"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            <button
              onClick={() => navigateLightbox('next')}
              aria-label="תמונה הבאה"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white hover:text-amber-400 transition-all duration-300 rounded-full p-3 backdrop-blur-sm border border-white/20 shadow-lg"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-lg font-semibold bg-black/50 px-4 py-2 rounded-full">
              {selectedImage.index + 1} / {images.length}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Gallery;